import useSWR from "swr";
import { changeRequestsService } from "../../api_client";
import {
  ChangeRequestDtoStatusEnum,
  ChangeRequestDtoTypeEnum,
  type ChangeRequestDto,
} from "../../../api";
import { useMePartner } from "../../../swr/useMePartner";

const PENDING_STATUSES: ChangeRequestDtoStatusEnum[] = [
  ChangeRequestDtoStatusEnum.Pending,
  ChangeRequestDtoStatusEnum.ChangesRequested,
];

const DRAFT_STATUSES: ChangeRequestDtoStatusEnum[] = [ChangeRequestDtoStatusEnum.Draft];

/** SWR key prefix shared by both hooks, so mutations can revalidate them together. */
export const MY_PROFILE_CHANGE_REQUEST_KEY = "my-profile-change-request";

/**
 * Fetches the current user's newest profile-update change request in one of
 * `statuses`, if any. Only the type matching the user's role (buyer vs.
 * seller) is requested — a buyer never has SELLER_PROFILE_UPDATE open and vice
 * versa, so issuing both fetches would just be wasted round-trips.
 *
 * The backend allows one open (draft / pending / changes-requested) request
 * per profile and lists newest first, so the open one, when there is one, is
 * always within the first few rows.
 */
function useMyProfileChangeRequest(statuses: ChangeRequestDtoStatusEnum[]) {
  const { isSeller, isBuyer, isLoading: isLoadingPartner } = useMePartner();
  const type = isSeller
    ? ChangeRequestDtoTypeEnum.SellerProfileUpdate
    : isBuyer
      ? ChangeRequestDtoTypeEnum.BuyerProfileUpdate
      : null;

  return useSWR<ChangeRequestDto | null>(
    type && !isLoadingPartner ? [MY_PROFILE_CHANGE_REQUEST_KEY, type, ...statuses] : null,
    async () => {
      const response = await changeRequestsService.findMine({
        type: type!,
        pageSize: 5,
      } as never);
      const rows = response.data.data ?? [];
      return (
        rows.find((r) => statuses.includes(r.status as ChangeRequestDtoStatusEnum)) ?? null
      );
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  );
}

/** The user's profile change request that is waiting for (or back from) admin review. */
export function useMyPendingProfileChangeRequest() {
  return useMyProfileChangeRequest(PENDING_STATUSES);
}

/** The user's saved, not yet submitted, profile change request. */
export function useMyDraftProfileChangeRequest() {
  return useMyProfileChangeRequest(DRAFT_STATUSES);
}
